import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'
import { genPrompt, getVideoPathByPrompt } from '@/api/promptVideo'
import { saveVideoSource } from '@/api/videoSource'
import { getOssClient } from '@/utils/oss'
import videoAnalysisWorkflow from '@/api/json_api/Qwen3.5-Omni视频反推.json'

// node19（ges_视频分析合并）是工作流终端输出节点，产出可直接入库的扁平 JSON
const MERGE_NODE_ID = '19'
// 轮询配置：视频反推（Qwen3.5-Omni）耗时较长，最长等待 10 分钟，每 3 秒查一次
const POLL_TIMEOUT_MS = 600000
const POLL_INTERVAL_MS = 3000
// 素材库显示文件（OSS 上的历史 JSON）：入库后把新素材追加进去，页面读它来显示
const FILE_OVERVIEW_URL = 'https://mj-comfy-ui.oss-cn-shenzhen.aliyuncs.com/vidi_videp_library/File_Overview.json'
const FILE_OVERVIEW_KEY = 'vidi_videp_library/File_Overview.json'

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// 尝试把任意值解析为「含 video_url 的对象」；兼容字符串化 JSON 与已解析对象
function tryParseMerged(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value.video_url ? value : null
  }
  if (typeof value === 'string') {
    const s = value.trim()
    if (!s.startsWith('{')) return null
    try {
      const obj = JSON.parse(s)
      return obj && typeof obj === 'object' && obj.video_url ? obj : null
    } catch (e) {
      return null
    }
  }
  return null
}

// 防御式提取：优先扫描 node19 的所有 ui 输出，兼容 ui key 名差异（merged_json / text / result …）
function extractMergedJson(outputs) {
  if (!outputs || typeof outputs !== 'object') return null
  const nodeIds = Object.keys(outputs).sort((a, b) => {
    if (a === MERGE_NODE_ID) return -1
    if (b === MERGE_NODE_ID) return 1
    return 0
  })
  for (const nodeId of nodeIds) {
    const nodeOut = outputs[nodeId]
    if (!nodeOut || typeof nodeOut !== 'object') continue
    for (const key of Object.keys(nodeOut)) {
      const val = nodeOut[key]
      if (Array.isArray(val)) {
        for (const item of val) {
          const merged = tryParseMerged(item)
          if (merged) return merged
        }
      } else {
        const merged = tryParseMerged(val)
        if (merged) return merged
      }
    }
  }
  return null
}

// 轮询 /history/{promptId} 直到取到合并 JSON（或工作流出错 / 超时）
async function pollMergedJson(promptId, onProgress) {
  const start = Date.now()
  while (Date.now() - start < POLL_TIMEOUT_MS) {
    let res
    try {
      res = await getVideoPathByPrompt(promptId)
    } catch (e) {
      // 偶发网络抖动不立即失败，继续轮询
      console.warn('[ingest] 轮询 history 异常，将继续重试:', e && e.message)
      await sleep(POLL_INTERVAL_MS)
      continue
    }
    const entry = res && res[promptId]
    if (entry) {
      const status = entry.status || {}
      if (status.status_str === 'error') {
        throw new Error('ComfyUI 工作流执行出错')
      }
      const merged = extractMergedJson(entry.outputs)
      if (merged) return merged
    }
    if (onProgress) onProgress('analyzing')
    await sleep(POLL_INTERVAL_MS)
  }
  throw new Error('视频分析超时，请稍后在素材库确认')
}

// 把一条合并 JSON 追加进素材库显示文件（读—去重—追加—写回 OSS）
// 注：整份文件读改写；单用户批量为顺序执行故安全，多人同时上传存在极小概率相互覆盖
async function appendToFileOverview(merged) {
  if (!merged || !merged.video_url) throw new Error('缺少 video_url，无法写入素材库文件')
  // 1) 读取现有文件（公开地址 + 时间戳防缓存）；读不到则从空模板开始
  let doc = null
  try {
    const res = await axios.get(FILE_OVERVIEW_URL, { params: { _t: Date.now() }, timeout: 15000 })
    if (res.data && typeof res.data === 'object') doc = res.data
  } catch (e) {
    doc = null
  }
  if (!doc || !Array.isArray(doc.entries)) {
    doc = { overview: 'vidi_videp_library 视频分析汇总', entries: [] }
  }
  const entries = doc.entries
  const nowIso = new Date().toISOString().slice(0, 19)
  const nextIndex = () => entries.reduce((m, e) => Math.max(m, Number(e && e.index) || 0), 0) + 1
  // 2) 构造新条目（扁平，剔除体积大的 raw_analysis）
  const entry = Object.assign({}, merged)
  delete entry.raw_analysis
  // 3) 按 video_url 去重：已存在则更新（保留原 index / added_at），否则追加
  const existIdx = entries.findIndex(e => e && e.video_url === merged.video_url)
  if (existIdx >= 0) {
    const old = entries[existIdx]
    entry.index = old.index != null ? old.index : nextIndex()
    entry.added_at = old.added_at || nowIso
    entries[existIdx] = entry
  } else {
    entry.index = nextIndex()
    entry.added_at = nowIso
    entries.push(entry)
  }
  // 4) 更新汇总元信息并写回 OSS（Blob 形式，浏览器端 ali-oss 支持）
  doc.total_entries = entries.length
  doc.last_updated = nowIso
  const ossClient = await getOssClient()
  const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' })
  await ossClient.put(FILE_OVERVIEW_KEY, blob)
}

/**
 * 视频素材「分析 → 入库」统一流程（Route A：前端中转）
 *   1) 深拷贝反推工作流并写入 video_url（node5.inputs.prompt）
 *   2) 提交 ComfyUI（genPrompt）拿 prompt_id
 *   3) 轮询 /history 取出 node19 合并 JSON
 *   4) 双写：追加进素材库显示文件 File_Overview.json（页面显示）+ POST 接口1 入库（拼接检索）
 *
 * 三个上传入口（VideoClip / VideoStitching / VideoSourceCenter）共用此方法。
 *
 * @param {string} videoUrl 视频 OSS 地址
 * @param {object} [options]
 * @param {(stage: string, payload?: any) => void} [options.onProgress]
 *        进度回调，stage ∈ submitting / submitted / analyzing / analyzed / saving / saved
 * @returns {Promise<object>} { merged, dbOk, saveRes }
 */
export async function ingestVideoToLibrary(videoUrl, options = {}) {
  const { onProgress } = options
  if (!videoUrl) throw new Error('视频 URL 不存在')

  // 1) 构造工作流副本并写入视频地址
  const workflowCopy = JSON.parse(JSON.stringify(videoAnalysisWorkflow))
  if (!workflowCopy['5'] || !workflowCopy['5'].inputs) {
    throw new Error('反推工作流结构异常（缺少 node5）')
  }
  workflowCopy['5'].inputs.prompt = videoUrl

  // 2) 提交任务
  if (onProgress) onProgress('submitting')
  const submitRes = await genPrompt({ prompt: workflowCopy, client_id: uuidv4() })
  const promptId = submitRes && submitRes.prompt_id
  if (!promptId) throw new Error('任务提交失败，未获得 prompt_id')
  if (onProgress) onProgress('submitted', { promptId })

  // 3) 轮询取合并 JSON
  const merged = await pollMergedJson(promptId, onProgress)
  if (onProgress) onProgress('analyzed', { merged })

  // 4) 双写：先写素材库显示文件（页面显示依赖它），再写数据库（接口1，供拼接检索）
  if (onProgress) onProgress('saving')
  // 4a) 显示文件（主）：写失败即视为入库失败，抛出让调用方提示
  await appendToFileOverview(merged)
  // 4b) 数据库（次）：best-effort，后端未就绪也不影响素材库显示
  let saveRes = null
  let dbOk = false
  try {
    saveRes = await saveVideoSource(merged)
    dbOk = true
  } catch (e) {
    console.warn('[ingest] 数据库入库失败（不影响素材库显示）:', e && e.message)
  }
  if (onProgress) onProgress('saved', { dbOk, saveRes })
  return { merged, dbOk, saveRes }
}
